import React, { useState, useRef, useCallback } from 'react';

interface DurationDialPickerProps {
  durationMinutes: number;
  onChange: (minutes: number) => void;
}

type Mode = 'hours' | 'minutes';

const DurationDialPicker: React.FC<DurationDialPickerProps> = ({
  durationMinutes,
  onChange,
}) => {
  const [mode, setMode] = useState<Mode>('hours');
  const svgRef = useRef<SVGSVGElement>(null);
  const isDragging = useRef(false);

  const hours = Math.floor(durationMinutes / 60);
  const minutes = durationMinutes % 60;

  const CENTER = 120;
  const RADIUS = 90;
  const INNER_RADIUS = 55;

  // Calculate angle from value
  const getAngleFromValue = (value: number, max: number): number => {
    return (value / max) * 360 - 90; // -90 to start from top
  };

  // Calculate value from angle
  const getValueFromAngle = (angle: number, max: number): number => {
    let normalizedAngle = angle + 90; // Adjust for top start
    if (normalizedAngle < 0) normalizedAngle += 360;
    if (normalizedAngle >= 360) normalizedAngle -= 360;
    return Math.round((normalizedAngle / 360) * max);
  };

  // Get coordinates from angle
  const getCoordinates = (angle: number, radius: number) => {
    const radians = (angle * Math.PI) / 180;
    return {
      x: CENTER + radius * Math.cos(radians),
      y: CENTER + radius * Math.sin(radians),
    };
  };

  // Handle pointer events
  const handlePointerEvent = useCallback(
    (event: React.PointerEvent | PointerEvent) => {
      if (!svgRef.current) return;

      const rect = svgRef.current.getBoundingClientRect();
      const x = event.clientX - rect.left - CENTER;
      const y = event.clientY - rect.top - CENTER;

      let angle = Math.atan2(y, x) * (180 / Math.PI);

      if (mode === 'hours') {
        // Hours: 0-5 (5 hours max for games)
        const newHours = Math.min(5, Math.max(0, getValueFromAngle(angle, 6)));
        onChange(newHours * 60 + minutes);
      } else {
        // Minutes: 0-59, snap to 5-minute intervals
        let newMinutes = getValueFromAngle(angle, 60);
        // Snap to nearest 5
        newMinutes = Math.round(newMinutes / 5) * 5;
        if (newMinutes >= 60) newMinutes = 0;
        onChange(hours * 60 + newMinutes);
      }
    },
    [mode, hours, minutes, onChange]
  );

  const handlePointerDown = (event: React.PointerEvent) => {
    isDragging.current = true;
    (event.target as Element).setPointerCapture(event.pointerId);
    handlePointerEvent(event);
  };

  const handlePointerMove = (event: React.PointerEvent) => {
    if (isDragging.current) {
      handlePointerEvent(event);
    }
  };

  const handlePointerUp = (event: React.PointerEvent) => {
    isDragging.current = false;
    (event.target as Element).releasePointerCapture(event.pointerId);
  };

  // Generate hour markers (0-5)
  const hourMarkers = Array.from({ length: 6 }, (_, i) => {
    const angle = getAngleFromValue(i, 6);
    const pos = getCoordinates(angle, RADIUS - 15);
    const isSelected = mode === 'hours' && hours === i;
    return (
      <g key={`hour-${i}`}>
        <circle
          cx={pos.x}
          cy={pos.y}
          r={isSelected ? 18 : 14}
          className={`transition-all duration-150 ${
            isSelected
              ? 'fill-primary-600'
              : 'fill-gray-100 hover:fill-primary-100'
          }`}
        />
        <text
          x={pos.x}
          y={pos.y}
          textAnchor="middle"
          dominantBaseline="central"
          className={`text-sm font-medium select-none pointer-events-none ${
            isSelected ? 'fill-white' : 'fill-gray-700'
          }`}
        >
          {i}h
        </text>
      </g>
    );
  });

  // Generate minute markers (every 5 minutes = 12 markers)
  const minuteMarkers = Array.from({ length: 12 }, (_, i) => {
    const value = i * 5;
    const angle = getAngleFromValue(value, 60);
    const pos = getCoordinates(angle, RADIUS - 15);
    const isSelected = mode === 'minutes' && Math.abs(minutes - value) < 3;
    return (
      <g key={`minute-${i}`}>
        <circle
          cx={pos.x}
          cy={pos.y}
          r={isSelected ? 18 : 14}
          className={`transition-all duration-150 ${
            isSelected
              ? 'fill-primary-600'
              : 'fill-gray-100 hover:fill-primary-100'
          }`}
        />
        <text
          x={pos.x}
          y={pos.y}
          textAnchor="middle"
          dominantBaseline="central"
          className={`text-xs font-medium select-none pointer-events-none ${
            isSelected ? 'fill-white' : 'fill-gray-700'
          }`}
        >
          {value.toString().padStart(2, '0')}
        </text>
      </g>
    );
  });

  // Calculate hand position
  const handAngle = mode === 'hours'
    ? getAngleFromValue(hours, 6)
    : getAngleFromValue(minutes, 60);
  const handEnd = getCoordinates(handAngle, INNER_RADIUS);

  // Format duration display
  const formatDuration = () => {
    if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h`;
    if (minutes > 0) return `${minutes}m`;
    return '0m';
  };

  return (
    <div className="flex flex-col items-center">
      {/* Mode Toggle */}
      <div className="flex mb-4 bg-surface-subtle rounded-lg p-1">
        <button
          type="button"
          onClick={() => setMode('hours')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
            mode === 'hours'
              ? 'bg-surface text-primary-700 shadow-sm'
              : 'text-body hover:text-heading'
          }`}
        >
          Hours
        </button>
        <button
          type="button"
          onClick={() => setMode('minutes')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
            mode === 'minutes'
              ? 'bg-surface text-primary-700 shadow-sm'
              : 'text-body hover:text-heading'
          }`}
        >
          Minutes
        </button>
      </div>

      {/* Clock Dial */}
      <svg
        ref={svgRef}
        width="240"
        height="240"
        viewBox="0 0 240 240"
        className="cursor-pointer touch-none"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
      >
        {/* Background circle */}
        <circle
          cx={CENTER}
          cy={CENTER}
          r={RADIUS + 10}
          className="fill-gray-50 stroke-gray-200"
          strokeWidth="2"
        />

        {/* Inner circle */}
        <circle
          cx={CENTER}
          cy={CENTER}
          r={INNER_RADIUS - 10}
          className="fill-white stroke-gray-100"
          strokeWidth="1"
        />

        {/* Clock hand */}
        <line
          x1={CENTER}
          y1={CENTER}
          x2={handEnd.x}
          y2={handEnd.y}
          className="stroke-primary-500"
          strokeWidth="3"
          strokeLinecap="round"
        />

        {/* Center dot */}
        <circle
          cx={CENTER}
          cy={CENTER}
          r="6"
          className="fill-primary-600"
        />

        {/* Markers */}
        {mode === 'hours' ? hourMarkers : minuteMarkers}

        {/* Center display */}
        <text
          x={CENTER}
          y={CENTER - 5}
          textAnchor="middle"
          dominantBaseline="central"
          className="fill-gray-900 text-2xl font-bold select-none pointer-events-none"
        >
          {formatDuration()}
        </text>
        <text
          x={CENTER}
          y={CENTER + 18}
          textAnchor="middle"
          dominantBaseline="central"
          className="fill-gray-400 text-xs select-none pointer-events-none"
        >
          {mode === 'hours' ? 'Select hours' : 'Select minutes'}
        </text>
      </svg>

      {/* Quick presets */}
      <div className="flex flex-wrap gap-2 mt-4 justify-center">
        {[15, 30, 45, 60, 90, 120, 180].map((preset) => (
          <button
            data-track="duration_preset"
            key={preset}
            type="button"
            onClick={() => onChange(preset)}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
              durationMinutes === preset
                ? 'bg-primary-600 text-white'
                : 'bg-surface-subtle text-body hover:bg-primary-100 hover:text-primary-700'
            }`}
          >
            {preset < 60 ? `${preset}m` : `${preset / 60}h`}
          </button>
        ))}
      </div>
    </div>
  );
};

export default DurationDialPicker;
